import React, { useContext, useEffect, useState, memo } from 'react'
import {
  Box,
  Typography,
  Avatar,
  Button,
  Chip,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material'
import Edit from '@mui/icons-material/Edit'
import Email from '@mui/icons-material/Email'
import Phone from '@mui/icons-material/Phone'
import LocationOn from '@mui/icons-material/LocationOn'
import CalendarToday from '@mui/icons-material/CalendarToday'
import Work from '@mui/icons-material/Work'
import School from '@mui/icons-material/School'
import Verified from '@mui/icons-material/Verified'
import Description from '@mui/icons-material/Description'
import { default as ImageIcon } from '@mui/icons-material/Image'
import ExpandMore from '@mui/icons-material/ExpandMore'
import Person from '@mui/icons-material/Person'
import Psychology from '@mui/icons-material/Psychology'
import LocalHospital from '@mui/icons-material/LocalHospital'
import Language from '@mui/icons-material/Language'
import AccessTime from '@mui/icons-material/AccessTime'
import WhatsApp from '@mui/icons-material/WhatsApp'
import Public from '@mui/icons-material/Public'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import Star from '@mui/icons-material/Star'
import TrendingUp from '@mui/icons-material/TrendingUp'
import Groups from '@mui/icons-material/Groups'
import Schedule from '@mui/icons-material/Schedule'
import Chat from '@mui/icons-material/Chat'
import Book from '@mui/icons-material/Book'
import VideoLibrary from '@mui/icons-material/VideoLibrary'
import Business from '@mui/icons-material/Business'
import Lock from '@mui/icons-material/Lock'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Cancel from '@mui/icons-material/Cancel'
import Info from '@mui/icons-material/Info'
import Translate from '@mui/icons-material/Translate'
import Favorite from '@mui/icons-material/Favorite'
import FavoriteBorder from '@mui/icons-material/FavoriteBorder'
import MonetizationOn from '@mui/icons-material/MonetizationOn'
import Settings from '@mui/icons-material/Settings'
import Event from '@mui/icons-material/Event'
import Category from '@mui/icons-material/Category'
import Article from '@mui/icons-material/Article'
import { styled } from '@mui/material/styles'
import { ProfileContext } from 'src/consumer/context/ProfileContext'
import { getImageLink } from 'src/mentor/helpers/utilityFunctions'
import { mediaURL } from 'src/consumer/services'
import dayjs from 'dayjs'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'
import { MentorSetupContext } from 'src/mentor/context/MentorSetupContext'
import { getMPDataWithoutParams } from 'src/mentor/services'
import { getUserSignInId } from 'src/shared/utils/accountIdentity'
import { isOnboardedAsDoctor, isOnboardedAsTherapist, getAllRolesInBoolean } from 'src/mentor/layout/utils'
import {
  allAvailableEventTypes,
  displayNamesOfEventTypes,
  RENDERING_ORDER_OF_DAYS,
  therapyDisciplineLabel,
} from 'src/consumer/constants'

const ProfileViewerContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '1200px',
  margin: '0 auto',
  padding: theme.spacing(3, 2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 1.5),
  },
}))

const ProfileHeader = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  border: '1px solid rgba(99, 102, 241, 0.2)',
  borderRadius: '24px',
  boxShadow: '0 8px 32px rgba(99, 102, 241, 0.15)',
  marginBottom: theme.spacing(3),
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(90deg, rgba(99, 102, 241, 0.8) 0%, rgba(139, 92, 246, 0.8) 100%)',
  },
}))

const ProfileHeaderContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  padding: theme.spacing(4),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    textAlign: 'center',
    padding: theme.spacing(3),
  },
}))

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  border: '4px solid rgba(255, 255, 255, 0.9)',
  boxShadow: '0 8px 24px rgba(99, 102, 241, 0.3)',
  [theme.breakpoints.down('sm')]: {
    width: 100,
    height: 100,
  },
}))

const ProfileInfo = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
}))

const ProfileName = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '32px',
  fontWeight: 700,
  color: '#1f2937',
  marginBottom: theme.spacing(0.5),
  [theme.breakpoints.down('sm')]: {
    fontSize: '24px',
  },
}))

const ProfileRole = styled(Box)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '16px',
  fontWeight: 500,
  color: '#6b7280',
  marginBottom: theme.spacing(1),
}))

const ProfileBio = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  fontWeight: 400,
  color: '#4b5563',
  lineHeight: 1.6,
  maxWidth: '600px',
}))

const EditButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.95) 0%, rgba(139, 92, 246, 0.95) 100%)',
  color: 'white',
  fontWeight: 600,
  padding: theme.spacing(1.25, 3),
  borderRadius: '12px',
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 1) 0%, rgba(139, 92, 246, 1) 100%)',
    boxShadow: '0 6px 16px rgba(99, 102, 241, 0.4)',
    transform: 'translateY(-2px)',
  },
  transition: 'all 0.2s ease',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    marginTop: theme.spacing(2),
  },
}))

const SectionCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(20px) saturate(180%)',
  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
  border: '1px solid rgba(99, 102, 241, 0.1)',
  borderRadius: '20px',
  boxShadow: '0 4px 20px rgba(99, 102, 241, 0.08)',
  marginBottom: theme.spacing(3),
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 32px rgba(99, 102, 241, 0.12)',
    transform: 'translateY(-2px)',
  },
}))

const SectionHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  borderBottom: '1px solid rgba(99, 102, 241, 0.1)',
  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}))

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '20px',
  fontWeight: 700,
  color: '#1f2937',
  flex: 1,
}))

const SectionContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}))

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  padding: theme.spacing(1.5, 0),
  borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
  '&:last-child': {
    borderBottom: 'none',
  },
}))

const InfoIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: '10px',
  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
  color: '#6366f1',
  flexShrink: 0,
}))

const InfoLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
  fontWeight: 600,
  color: '#6b7280',
  minWidth: '140px',
  [theme.breakpoints.down('sm')]: {
    minWidth: '100px',
    fontSize: '13px',
  },
}))

const InfoValue = styled(Box)(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  fontWeight: 500,
  color: '#1f2937',
  flex: 1,
  wordBreak: 'break-word',
}))

const ChipContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: theme.spacing(0.5),
}))

const StyledChip = styled(Chip)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
  color: '#6366f1',
  fontWeight: 600,
  fontSize: '13px',
  border: '1px solid rgba(99, 102, 241, 0.2)',
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
  },
}))

const CertificateItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1.5, 2),
  borderRadius: '12px',
  background: 'rgba(99, 102, 241, 0.05)',
  border: '1px solid rgba(99, 102, 241, 0.1)',
  marginBottom: theme.spacing(1.5),
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'rgba(99, 102, 241, 0.08)',
    transform: 'translateX(4px)',
  },
  '&:last-child': {
    marginBottom: 0,
  },
}))

const CertificateLink = styled('a')(({ theme }) => ({
  fontFamily: "'Inter', sans-serif",
  fontSize: '15px',
  fontWeight: 600,
  color: '#6366f1',
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '&:hover': {
    textDecoration: 'underline',
  },
}))

const ImageGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
  gap: theme.spacing(2),
  marginTop: theme.spacing(2),
}))

const ImageCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
  },
}))

const ImageCardMedia = styled('img')({
  width: '100%',
  height: '200px',
  objectFit: 'cover',
})

const EmptyState = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  color: '#9ca3af',
  fontFamily: "'Inter', sans-serif",
  fontSize: '14px',
}))

function ProfileViewer({
  onEdit,
  profileData: propProfileData,
  allTags: propAllTags,
  readApiWithoutParams = getMPDataWithoutParams,
  techniqueApiPathBuilder = (techniqueId) => `/api/secure/technique/${techniqueId}`,
  certificateListApiPathBuilder = () => '/api/secure/teacher/certificate-record/list/paginated?page=0&size=100',
}) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const mentorProfileContext = useContext(MentorProfileContext)
  const mentorSetupContext = useContext(MentorSetupContext)
  const profileContext = useContext(ProfileContext)
  const currentUserProfileData = profileContext?.profileData
  
  // Use props if provided, otherwise use context (context may be null in support portal)
  const mentorProfileData = propProfileData || mentorProfileContext?.mentorProfileData
  const allTags = propAllTags || mentorSetupContext?.allTags
  const signInId = getUserSignInId(currentUserProfileData || mentorProfileData)
  const [techniques, setTechniques] = useState([])
  const [certificates, setCertificates] = useState([])
  const [loadingCertificates, setLoadingCertificates] = useState(true)

  // Fetch techniques
  useEffect(() => {
    async function fetchTechniques() {
      if (mentorProfileData?.personalize?.techniquesSpecializedIn?.length > 0) {
        try {
          const techniquePromises = mentorProfileData.personalize.techniquesSpecializedIn.map(
            async (techniqueId) => {
              const resp = await readApiWithoutParams(techniqueApiPathBuilder(techniqueId))
              if (resp.status === 200) {
                return resp.data
              }
              return null
            },
          )
          const fetchedTechniques = await Promise.all(techniquePromises)
          setTechniques(fetchedTechniques.filter((t) => t !== null))
        } catch (error) {
          console.error('Error fetching techniques:', error)
        }
      }
    }
    fetchTechniques()
  }, [mentorProfileData?.personalize?.techniquesSpecializedIn, readApiWithoutParams, techniqueApiPathBuilder])

  // Fetch certificates
  useEffect(() => {
    async function fetchCertificates() {
      setLoadingCertificates(true)
      try {
        const response = await readApiWithoutParams(
          certificateListApiPathBuilder(mentorProfileData),
        )
        if (response.status === 200) {
          setCertificates(response.data.content || [])
        }
      } catch (error) {
        console.error('Error fetching certificates:', error)
      } finally {
        setLoadingCertificates(false)
      }
    }
    fetchCertificates()
  }, [certificateListApiPathBuilder, mentorProfileData, readApiWithoutParams])

  // Get category display names
  const getCategoryDisplayName = (categoryId) => {
    const category = allTags?.find((tag) => tag.id === categoryId)
    return category?.name || category?.title || categoryId
  }

  // Get specialization display names
  const getSpecializationDisplayName = (specializationId) => {
    const specialization = allTags?.find((tag) => tag.id === specializationId)
    return specialization?.name || specialization?.title || specializationId
  }

  // Calculate experience
  const getExperienceText = () => {
    if (
      !mentorProfileData?.personalize?.experienceStartMonth ||
      !mentorProfileData?.personalize?.experienceStartYear
    ) {
      return 'Not specified'
    }

    const startDate = dayjs(
      `${mentorProfileData.personalize.experienceStartYear}-${mentorProfileData.personalize.experienceStartMonth}-01`,
    )
    const now = dayjs()
    const years = now.diff(startDate, 'years')
    const months = now.diff(startDate.add(years, 'years'), 'months')

    if (years === 0 && months === 0) {
      return 'Just started'
    }
    if (years === 0) {
      return `${months} ${months === 1 ? 'month' : 'months'}`
    }
    if (months === 0) {
      return `${years} ${years === 1 ? 'year' : 'years'}`
    }
    return `${years} ${years === 1 ? 'year' : 'years'} ${months} ${months === 1 ? 'month' : 'months'}`
  }

  // Format date of birth
  const formatDOB = () => {
    if (!mentorProfileData?.dob) return 'Not specified'
    return dayjs(mentorProfileData.dob).format('MMMM DD, YYYY')
  }

  // Get gender display
  const getGenderDisplay = () => {
    if (!mentorProfileData?.gender) return 'Not specified'
    return mentorProfileData.gender.charAt(0).toUpperCase() + mentorProfileData.gender.slice(1)
  }

  // Get timezone display
  const getTimezoneDisplay = () => {
    if (!mentorProfileData?.timeZone?.title) return 'Not specified'
    return mentorProfileData.timeZone.title
  }

  // Get location display
  const getLocationDisplay = () => {
    const parts = []
    if (mentorProfileData?.location?.city) parts.push(mentorProfileData.location.city)
    if (mentorProfileData?.location?.state) parts.push(mentorProfileData.location.state)
    if (mentorProfileData?.location?.country) parts.push(mentorProfileData.location.country)
    return parts.length > 0 ? parts.join(', ') : 'Not specified'
  }

  // Get languages display
  const getLanguagesDisplay = () => {
    if (!mentorProfileData?.languages || mentorProfileData.languages.length === 0) {
      return 'Not specified'
    }
    return mentorProfileData.languages
      .map((lang) => lang.charAt(0).toUpperCase() + lang.slice(1))
      .join(', ')
  }

  // Get registration ID type name
  const getRegIdTypeName = (typeId) => {
    // This would need to be fetched or mapped from constants
    return typeId || 'Registration ID'
  }

  // Resolve primary contact details (following practitioner.md logic)
  const resolvePrimaryContactDetails = () => {
    const commPref = mentorProfileData?.communicationPref || {}
    const resolved = {
      email: null,
      whatsappId: null,
      phone: null,
    }

    // Email: communicationPref.email.uid → base email → outGoingAumhumEmail
    if (commPref?.email?.enabled && commPref?.email?.uid) {
      resolved.email = commPref.email.uid
    } else if (mentorProfileData?.email) {
      resolved.email = mentorProfileData.email
    } else if (mentorProfileData?.outGoingAumhumEmail) {
      resolved.email = mentorProfileData.outGoingAumhumEmail
    }

    // WhatsApp: communicationPref.whatsapp.uid (drop + prefix)
    if (commPref?.whatsapp?.enabled && commPref?.whatsapp?.uid) {
      resolved.whatsappId = commPref.whatsapp.uid.replace(/^\+/, '')
    }

    // Phone: communicationPref.voiceCall.uid → base mobile (prefix with +)
    if (commPref?.voiceCall?.enabled && commPref?.voiceCall?.uid) {
      resolved.phone = commPref.voiceCall.uid.startsWith('+')
        ? commPref.voiceCall.uid
        : `+${commPref.voiceCall.uid}`
    } else if (mentorProfileData?.mobile) {
      resolved.phone = mentorProfileData.mobile.startsWith('+')
        ? mentorProfileData.mobile
        : `+${mentorProfileData.mobile}`
    }

    return resolved
  }

  const resolvedContact = resolvePrimaryContactDetails()

  // Get user roles
  const userRoles = getAllRolesInBoolean()
  const isTeacher = userRoles?.isTeacher
  const isCreator = userRoles?.isCreator
  const getRoleLabels = () => {
    const roles = []
    if (userRoles?.isDoctor) roles.push('Doctor')
    if (userRoles?.isTherapist) roles.push('Therapist')
    if (userRoles?.isScreener) roles.push('Screener')
    return roles
  }

  // Get KYC status display
  const getKycStatusDisplay = () => {
    const status = mentorProfileData?.kycStatus
    if (!status) return { text: 'Not submitted', color: '#9ca3af' }
    switch (status.toLowerCase()) {
      case 'approved':
        return { text: 'Approved', color: '#10b981' }
      case 'pending':
        return { text: 'Pending Review', color: '#f59e0b' }
      case 'rejected':
        return { text: 'Rejected', color: '#ef4444' }
      default:
        return { text: status, color: '#6b7280' }
    }
  }

  // Get KYC approved time display
  const getKycApprovedSince = () => {
    if (!mentorProfileData?.approvedTime) return null
    try {
      return dayjs(mentorProfileData.approvedTime).format('MMMM DD, YYYY')
    } catch (error) {
      return mentorProfileData.approvedTime
    }
  }

  // Get welcome message display
  const getWelcomeMessage = (type) => {
    const messages = {
      general: mentorProfileData?.welcomeMsg,
      chat: mentorProfileData?.chatWelcomeMsg,
      plan: mentorProfileData?.planWelcomeMsg,
      course: mentorProfileData?.courseWelcomeMsg,
    }
    return messages[type] || 'Not set'
  }

  // Format schedule display - properly render schedule objects with weeklyHourMap
  const formatScheduleDisplay = (schedule) => {
    if (!schedule || typeof schedule !== 'object') return []
    
    const scheduleItems = []
    const weeklyHourMap = schedule.weeklyHourMap || {}
    const timeZone = schedule.timeZone
      ? decodeURIComponent(schedule.timeZone).replace('%2B', '+').replace('%3A', ':')
      : null
    
    // Use RENDERING_ORDER_OF_DAYS to maintain consistent order
    const daysOrder = RENDERING_ORDER_OF_DAYS || [
      'sunday',
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
    ]
    
    daysOrder.forEach((day) => {
      const dayData = weeklyHourMap[day]
      if (dayData) {
        const isAvailable = dayData.available && dayData.slotList && dayData.slotList.length > 0
        const dayTitle = dayData.title || day.charAt(0).toUpperCase() + day.slice(1)
        
        if (isAvailable) {
          const slots = dayData.slotList
            .map((slot) => {
              const start = slot.startTime ? slot.startTime.substring(0, 5) : 'N/A'
              const end = slot.endTime ? slot.endTime.substring(0, 5) : 'N/A'
              return `${start}–${end}`
            })
            .join(', ')
          scheduleItems.push({
            day: dayTitle,
            slots,
            available: true,
          })
        } else {
          scheduleItems.push({
            day: dayTitle,
            slots: 'Closed',
            available: false,
          })
        }
      }
    })
    
    return {
      title: schedule.title || 'Schedule',
      isDefault: schedule.defaultSchedule || false,
      items: scheduleItems,
      timeZone,
      contextId: schedule.contextId,
    }
  }

  // Format event type display - properly render eventType objects
  const formatEventType = (eventType) => {
    if (!eventType || typeof eventType !== 'object') return null
    
    const type = eventType.type || 'unknown'
    const typeDisplayName =
      displayNamesOfEventTypes[type] ||
      type
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    
    const title = eventType.title || typeDisplayName
    const duration = eventType.durationMinutes ? `${eventType.durationMinutes} min` : null
    const price = eventType.price?.amount
      ? `${eventType.price.currency || 'INR'} ${eventType.price.amount}`
      : null
    
    // Determine meeting type from meetingSrc
    const meetingSrc = eventType.meetingSrc
    const meetingType =
      meetingSrc === 'aumhum' ? 'Online/Video' : meetingSrc === 'offline' ? 'In Person' : null
    
    const specialization = eventType.specializationId || null
    const bookingWindow = eventType.bookingWindowDays ? `${eventType.bookingWindowDays} days` : null
    
    return {
      id: eventType.id || eventType.uid,
      type,
      typeDisplayName,
      title,
      duration,
      price,
      meetingType,
      meetingSrc,
      specialization,
      bookingWindow,
      contextId: eventType.contextId,
    }
  }

  if (!mentorProfileData) {
    return (
      <ProfileViewerContainer>
        <EmptyState>Loading profile data...</EmptyState>
      </ProfileViewerContainer>
    )
  }

  return (
    <ProfileViewerContainer>
      {/* Profile Header */}
      <ProfileHeader>
        <ProfileHeaderContent>
          <ProfileAvatar src={getImageLink(mentorProfileData?.profilePicId)}>
            {mentorProfileData?.firstName?.charAt(0) || mentorProfileData?.name?.charAt(0) || 'U'}
          </ProfileAvatar>
          <ProfileInfo>
            <ProfileName>
              {mentorProfileData?.firstName || mentorProfileData?.name || 'Unnamed Profile'}{' '}
              {mentorProfileData?.lastName || ''}
            </ProfileName>
            <ProfileRole>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {getRoleLabels().length > 0 && (
                  <>
                    {getRoleLabels().map((role) => (
                      <Chip
                        key={role}
                        label={role}
                        size="small"
                        sx={{
                          backgroundColor: '#6366f1',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '12px',
                        }}
                      />
                    ))}
                    {mentorProfileData?.categories?.length > 0 && <span>·</span>}
                  </>
                )}
                {mentorProfileData?.categories
                  ?.map((cat) => getCategoryDisplayName(cat))
                  .filter(Boolean)
                  .join(', ') || 'Practitioner'}
              </Box>
            </ProfileRole>
            {mentorProfileData?.shortDesc && (
              <ProfileBio>{mentorProfileData.shortDesc}</ProfileBio>
            )}
          </ProfileInfo>
          {onEdit && (
            <EditButton
              startIcon={<Edit />}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onEdit()
              }}
            >
              Edit Profile
            </EditButton>
          )}
        </ProfileHeaderContent>
      </ProfileHeader>

      {/* Metrics & Status Section */}
      {(mentorProfileData?.level ||
        mentorProfileData?.studentsCount !== undefined ||
        mentorProfileData?.hrsTaught !== undefined ||
        mentorProfileData?.rating !== undefined ||
        mentorProfileData?.kycStatus) && (
        <SectionCard>
          <SectionHeader>
            <TrendingUp sx={{ color: '#6366f1', fontSize: 24 }} />
            <SectionTitle>Metrics & Status</SectionTitle>
          </SectionHeader>
          <SectionContent>
            {mentorProfileData?.kycStatus && (
              <InfoRow>
                <InfoIcon>
                  <Verified sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>KYC Status</InfoLabel>
                <InfoValue>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={getKycStatusDisplay().text}
                      size="small"
                      sx={{
                        backgroundColor: getKycStatusDisplay().color + '20',
                        color: getKycStatusDisplay().color,
                        fontWeight: 600,
                      }}
                    />
                    {(mentorProfileData?.kycStatus?.toLowerCase() === 'approved' ||
                      mentorProfileData?.kycStatus === 'approved') &&
                      getKycApprovedSince() && (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '13px' }}>
                          Approved since {getKycApprovedSince()}
                        </Typography>
                      )}
                  </Box>
                </InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.level !== undefined && (
              <InfoRow>
                <InfoIcon>
                  <Star sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Level</InfoLabel>
                <InfoValue>{mentorProfileData.level}</InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.studentsCount !== undefined && (
              <InfoRow>
                <InfoIcon>
                  <Groups sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Students Count</InfoLabel>
                <InfoValue>{mentorProfileData.studentsCount.toLocaleString()}</InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.hrsTaught !== undefined && isTeacher && !isCreator && (
              <InfoRow>
                <InfoIcon>
                  <AccessTime sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Hours Taught</InfoLabel>
                <InfoValue>{mentorProfileData.hrsTaught.toLocaleString()} hours</InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.rating !== undefined && mentorProfileData?.noOfRating > 0 && (
              <InfoRow>
                <InfoIcon>
                  <Star sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Rating</InfoLabel>
                <InfoValue>
                  {mentorProfileData.rating.toFixed(1)} ({mentorProfileData.noOfRating} ratings)
                </InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.liveSessionsCount !== undefined && (
              <InfoRow>
                <InfoIcon>
                  <VideoLibrary sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Live Sessions</InfoLabel>
                <InfoValue>{mentorProfileData.liveSessionsCount.toLocaleString()}</InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.favCount !== undefined && isTeacher && !isCreator && (
              <InfoRow>
                <InfoIcon>
                  <Favorite sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Favorites</InfoLabel>
                <InfoValue>{mentorProfileData.favCount.toLocaleString()}</InfoValue>
              </InfoRow>
            )}
          </SectionContent>
        </SectionCard>
      )}

      {/* Personal Information Section */}
      <SectionCard>
        <SectionHeader>
          <Person sx={{ color: '#6366f1', fontSize: 24 }} />
          <SectionTitle>Personal Information</SectionTitle>
        </SectionHeader>
        <SectionContent>
          <InfoRow>
            <InfoIcon>
              <Lock sx={{ fontSize: 20 }} />
            </InfoIcon>
            <InfoLabel>Sign-in ID</InfoLabel>
            <InfoValue>{signInId || 'Not specified'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoIcon>
              <Email sx={{ fontSize: 20 }} />
            </InfoIcon>
            <InfoLabel>Email</InfoLabel>
            <InfoValue>{mentorProfileData?.email || 'Not specified'}</InfoValue>
          </InfoRow>
          {mentorProfileData?.mobile && (
            <InfoRow>
              <InfoIcon>
                <Phone sx={{ fontSize: 20 }} />
              </InfoIcon>
              <InfoLabel>Phone</InfoLabel>
              <InfoValue>{mentorProfileData.mobile}</InfoValue>
            </InfoRow>
          )}
          <InfoRow>
            <InfoIcon>
              <CalendarToday sx={{ fontSize: 20 }} />
            </InfoIcon>
            <InfoLabel>Date of Birth</InfoLabel>
            <InfoValue>{formatDOB()}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoIcon>
              <Person sx={{ fontSize: 20 }} />
            </InfoIcon>
            <InfoLabel>Gender</InfoLabel>
            <InfoValue>{getGenderDisplay()}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoIcon>
              <LocationOn sx={{ fontSize: 20 }} />
            </InfoIcon>
            <InfoLabel>Location</InfoLabel>
            <InfoValue>{getLocationDisplay()}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoIcon>
              <AccessTime sx={{ fontSize: 20 }} />
            </InfoIcon>
            <InfoLabel>Timezone</InfoLabel>
            <InfoValue>{getTimezoneDisplay()}</InfoValue>
          </InfoRow>
          {mentorProfileData?.languages && mentorProfileData.languages.length > 0 && (
            <InfoRow>
              <InfoIcon>
                <Language sx={{ fontSize: 20 }} />
              </InfoIcon>
              <InfoLabel>Languages</InfoLabel>
              <InfoValue>{getLanguagesDisplay()}</InfoValue>
            </InfoRow>
          )}
        </SectionContent>
      </SectionCard>

      {/* Contact & Communication Section */}
      <SectionCard>
        <SectionHeader>
          <Phone sx={{ color: '#6366f1', fontSize: 24 }} />
          <SectionTitle>Contact & Communication</SectionTitle>
        </SectionHeader>
        <SectionContent>
          {resolvedContact.email && (
            <InfoRow>
              <InfoIcon>
                <Email sx={{ fontSize: 20 }} />
              </InfoIcon>
              <InfoLabel>Primary Email</InfoLabel>
              <InfoValue>{resolvedContact.email}</InfoValue>
            </InfoRow>
          )}
          {resolvedContact.phone && (
            <InfoRow>
              <InfoIcon>
                <Phone sx={{ fontSize: 20 }} />
              </InfoIcon>
              <InfoLabel>Primary Phone</InfoLabel>
              <InfoValue>{resolvedContact.phone}</InfoValue>
            </InfoRow>
          )}
          {resolvedContact.whatsappId && (
            <InfoRow>
              <InfoIcon>
                <WhatsApp sx={{ fontSize: 20 }} />
              </InfoIcon>
              <InfoLabel>WhatsApp ID</InfoLabel>
              <InfoValue>{resolvedContact.whatsappId}</InfoValue>
            </InfoRow>
          )}
          {mentorProfileData?.communicationPref && (
            <>
              <InfoRow>
                <InfoIcon>
                  <Settings sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Communication Preferences</InfoLabel>
                <InfoValue>
                  <ChipContainer>
                    {mentorProfileData.communicationPref?.whatsapp?.enabled && (
                      <StyledChip
                        icon={<WhatsApp sx={{ fontSize: 16 }} />}
                        label="WhatsApp"
                        size="small"
                      />
                    )}
                    {mentorProfileData.communicationPref?.voiceCall?.enabled && (
                      <StyledChip
                        icon={<Phone sx={{ fontSize: 16 }} />}
                        label="Voice Call"
                        size="small"
                      />
                    )}
                    {mentorProfileData.communicationPref?.email?.enabled && (
                      <StyledChip
                        icon={<Email sx={{ fontSize: 16 }} />}
                        label="Email"
                        size="small"
                      />
                    )}
                  </ChipContainer>
                </InfoValue>
              </InfoRow>
            </>
          )}
          {mentorProfileData?.connectedCalendars &&
            mentorProfileData.connectedCalendars.length > 0 && (
              <InfoRow>
                <InfoIcon>
                  <CalendarToday sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Connected Calendars</InfoLabel>
                <InfoValue>
                  {mentorProfileData.connectedCalendars.length} calendar(s) connected
                </InfoValue>
              </InfoRow>
            )}
        </SectionContent>
      </SectionCard>

      {/* Professional Details Section */}
      <SectionCard>
        <SectionHeader>
          {isOnboardedAsDoctor() ? (
            <LocalHospital sx={{ color: '#6366f1', fontSize: 24 }} />
          ) : (
            <Psychology sx={{ color: '#6366f1', fontSize: 24 }} />
          )}
          <SectionTitle>Professional Details</SectionTitle>
        </SectionHeader>
        <SectionContent>
          {mentorProfileData?.categories && mentorProfileData.categories.length > 0 && (
            <InfoRow>
              <InfoIcon>
                <Work sx={{ fontSize: 20 }} />
              </InfoIcon>
              <InfoLabel>Categories</InfoLabel>
              <InfoValue>
                <ChipContainer>
                  {mentorProfileData.categories.map((category) => (
                    <StyledChip
                      key={category}
                      label={getCategoryDisplayName(category)}
                      size="small"
                    />
                  ))}
                </ChipContainer>
              </InfoValue>
            </InfoRow>
          )}
          {mentorProfileData?.specializedCategories &&
            mentorProfileData.specializedCategories.length > 0 && (
              <InfoRow>
                <InfoIcon>
                  <Verified sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Specializations</InfoLabel>
                <InfoValue>
                  <ChipContainer>
                    {mentorProfileData.specializedCategories.map((spec) => (
                      <StyledChip
                        key={spec}
                        label={getSpecializationDisplayName(spec)}
                        size="small"
                      />
                    ))}
                  </ChipContainer>
                </InfoValue>
              </InfoRow>
            )}
          {techniques.length > 0 && (
            <InfoRow>
              <InfoIcon>
                <Psychology sx={{ fontSize: 20 }} />
              </InfoIcon>
              <InfoLabel>Techniques</InfoLabel>
              <InfoValue>
                <ChipContainer>
                  {techniques.map((technique) => (
                    <StyledChip key={technique?.id} label={technique?.title} size="small" />
                  ))}
                </ChipContainer>
              </InfoValue>
            </InfoRow>
          )}
        </SectionContent>
      </SectionCard>

      {/* Qualifications & Experience Section */}
      <SectionCard>
        <SectionHeader>
          <School sx={{ color: '#6366f1', fontSize: 24 }} />
          <SectionTitle>Qualifications & Experience</SectionTitle>
        </SectionHeader>
        <SectionContent>
          {mentorProfileData?.personalize?.qualifications &&
            mentorProfileData.personalize.qualifications.length > 0 && (
              <InfoRow>
                <InfoIcon>
                  <School sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Qualifications</InfoLabel>
                <InfoValue>
                  <ChipContainer>
                    {mentorProfileData.personalize.qualifications.map((qual, idx) => (
                      <StyledChip key={idx} label={qual} size="small" />
                    ))}
                  </ChipContainer>
                </InfoValue>
              </InfoRow>
            )}
          <InfoRow>
            <InfoIcon>
              <Work sx={{ fontSize: 20 }} />
            </InfoIcon>
            <InfoLabel>Experience</InfoLabel>
            <InfoValue>{getExperienceText()}</InfoValue>
          </InfoRow>
          {mentorProfileData?.personalize?.experienceStartMonth &&
            mentorProfileData?.personalize?.experienceStartYear && (
              <InfoRow>
                <InfoIcon>
                  <CalendarToday sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Started Working</InfoLabel>
                <InfoValue>
                  {dayjs(
                    `${mentorProfileData.personalize.experienceStartYear}-${mentorProfileData.personalize.experienceStartMonth}-01`,
                  ).format('MMMM YYYY')}
                </InfoValue>
              </InfoRow>
            )}
        </SectionContent>
      </SectionCard>

      {/* Registration IDs Section */}
      {mentorProfileData?.registrationIds && mentorProfileData.registrationIds.length > 0 && (
        <SectionCard>
          <SectionHeader>
            <Verified sx={{ color: '#6366f1', fontSize: 24 }} />
            <SectionTitle>Registration Details</SectionTitle>
          </SectionHeader>
          <SectionContent>
            {mentorProfileData.registrationIds.map((regId, idx) => (
              <InfoRow key={idx}>
                <InfoIcon>
                  <Verified sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>{getRegIdTypeName(regId?.typeId)}</InfoLabel>
                <InfoValue>{regId?.id || 'Not provided'}</InfoValue>
              </InfoRow>
            ))}
          </SectionContent>
        </SectionCard>
      )}

      {/* Certifications Section */}
      <SectionCard>
        <SectionHeader>
          <Description sx={{ color: '#6366f1', fontSize: 24 }} />
          <SectionTitle>Certifications</SectionTitle>
        </SectionHeader>
        <SectionContent>
          {loadingCertificates ? (
            <EmptyState>Loading certificates...</EmptyState>
          ) : certificates.length > 0 ? (
            certificates.map((certificate, idx) => (
              <CertificateItem key={idx}>
                <CertificateLink
                  href={`${mediaURL}${certificate?.fileUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Description sx={{ fontSize: 18 }} />
                  {certificate?.title}
                </CertificateLink>
              </CertificateItem>
            ))
          ) : (
            <EmptyState>No certifications added yet</EmptyState>
          )}
        </SectionContent>
      </SectionCard>

      {/* Services Offered Section */}
      {mentorProfileData?.services && (
        <SectionCard>
          <SectionHeader>
            <Work sx={{ color: '#6366f1', fontSize: 24 }} />
            <SectionTitle>Services Offered</SectionTitle>
          </SectionHeader>
          <SectionContent>
            <ChipContainer>
              {mentorProfileData.services?.live && (
                <StyledChip label="1-on-1 Sessions" size="medium" />
              )}
              {mentorProfileData.services?.groupLive && (
                <StyledChip label="Group Sessions" size="medium" />
              )}
              {mentorProfileData.services?.recorded && (
                <StyledChip label="Published Content" size="medium" />
              )}
            </ChipContainer>
            {!mentorProfileData.services?.live &&
              !mentorProfileData.services?.groupLive &&
              !mentorProfileData.services?.recorded && (
                <EmptyState>No services configured</EmptyState>
              )}
          </SectionContent>
        </SectionCard>
      )}

      {/* Images Section */}
      {(mentorProfileData?.heroCardImage || mentorProfileData?.profilePicId) && (
        <SectionCard>
          <SectionHeader>
            <ImageIcon sx={{ color: '#6366f1', fontSize: 24 }} />
            <SectionTitle>Profile Images</SectionTitle>
          </SectionHeader>
          <SectionContent>
            <ImageGrid>
              {mentorProfileData?.heroCardImage && (
                <ImageCard>
                  <ImageCardMedia
                    src={getImageLink(mentorProfileData.heroCardImage)}
                    alt="Hero Image"
                  />
                  <CardContent>
                    <Typography variant="body2" fontWeight={600} color="text.secondary">
                      Hero Image
                    </Typography>
                  </CardContent>
                </ImageCard>
              )}
              {mentorProfileData?.profilePicId && (
                <ImageCard>
                  <ImageCardMedia
                    src={getImageLink(mentorProfileData.profilePicId)}
                    alt="Profile Picture"
                  />
                  <CardContent>
                    <Typography variant="body2" fontWeight={600} color="text.secondary">
                      Profile Picture
                    </Typography>
                  </CardContent>
                </ImageCard>
              )}
            </ImageGrid>
          </SectionContent>
        </SectionCard>
      )}

      {/* Branding & Content Section */}
      {(mentorProfileData?.longDesc ||
        mentorProfileData?.welcomeMsg ||
        mentorProfileData?.chatWelcomeMsg ||
        mentorProfileData?.planWelcomeMsg ||
        mentorProfileData?.courseWelcomeMsg ||
        mentorProfileData?.onboardingImage ||
        mentorProfileData?.backgroundImage) && (
        <SectionCard>
          <SectionHeader>
            <Article sx={{ color: '#6366f1', fontSize: 24 }} />
            <SectionTitle>Branding & Content</SectionTitle>
          </SectionHeader>
          <SectionContent>
            {mentorProfileData?.longDesc && (
              <InfoRow>
                <InfoIcon>
                  <Description sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Long Description</InfoLabel>
                <InfoValue sx={{ whiteSpace: 'pre-wrap' }}>{mentorProfileData.longDesc}</InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.welcomeMsg && (
              <InfoRow>
                <InfoIcon>
                  <Chat sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Welcome Message</InfoLabel>
                <InfoValue>{mentorProfileData.welcomeMsg}</InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.chatWelcomeMsg && (
              <InfoRow>
                <InfoIcon>
                  <Chat sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Chat Welcome Message</InfoLabel>
                <InfoValue>{mentorProfileData.chatWelcomeMsg}</InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.planWelcome && mentorProfileData?.planWelcomeMsg && (
              <InfoRow>
                <InfoIcon>
                  <Book sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Plan Welcome Message</InfoLabel>
                <InfoValue>{mentorProfileData.planWelcomeMsg}</InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.courseWelcome && mentorProfileData?.courseWelcomeMsg && (
              <InfoRow>
                <InfoIcon>
                  <School sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Course Welcome Message</InfoLabel>
                <InfoValue>{mentorProfileData.courseWelcomeMsg}</InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.planSectionTitle && (
              <InfoRow>
                <InfoIcon>
                  <Category sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Plan Section Title</InfoLabel>
                <InfoValue>{mentorProfileData.planSectionTitle}</InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.categoryPlaceholder && (
              <InfoRow>
                <InfoIcon>
                  <Category sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Category Placeholder</InfoLabel>
                <InfoValue>{mentorProfileData.categoryPlaceholder}</InfoValue>
              </InfoRow>
            )}
            {(mentorProfileData?.onboardingImage || mentorProfileData?.backgroundImage) && (
              <InfoRow>
                <InfoIcon>
                  <ImageIcon sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Branding Images</InfoLabel>
                <InfoValue>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                    {mentorProfileData?.onboardingImage && (
                      <ImageCard sx={{ maxWidth: 200 }}>
                        <ImageCardMedia
                          src={getImageLink(mentorProfileData.onboardingImage)}
                          alt="Onboarding Image"
                        />
                        <CardContent>
                          <Typography variant="body2" fontWeight={600} color="text.secondary">
                            Onboarding Image
                          </Typography>
                        </CardContent>
                      </ImageCard>
                    )}
                    {mentorProfileData?.backgroundImage && (
                      <ImageCard sx={{ maxWidth: 200 }}>
                        <ImageCardMedia
                          src={getImageLink(mentorProfileData.backgroundImage)}
                          alt="Background Image"
                        />
                        <CardContent>
                          <Typography variant="body2" fontWeight={600} color="text.secondary">
                            Background Image
                          </Typography>
                        </CardContent>
                      </ImageCard>
                    )}
                  </Box>
                </InfoValue>
              </InfoRow>
            )}
          </SectionContent>
        </SectionCard>
      )}

      {/* Content Categories & Specializations Section */}
      {(mentorProfileData?.contentCategories?.length > 0 ||
        mentorProfileData?.defaultContentCategory ||
        mentorProfileData?.masteredMeditationIds?.length > 0 ||
        mentorProfileData?.meditationIds?.length > 0) && (
        <SectionCard>
          <SectionHeader>
            <Category sx={{ color: '#6366f1', fontSize: 24 }} />
            <SectionTitle>Content Categories & Specializations</SectionTitle>
          </SectionHeader>
          <SectionContent>
            {mentorProfileData?.contentCategories?.length > 0 && (
              <InfoRow>
                <InfoIcon>
                  <Category sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Content Categories</InfoLabel>
                <InfoValue>
                  <ChipContainer>
                    {mentorProfileData.contentCategories.map((cat) => (
                      <StyledChip key={cat} label={cat} size="small" />
                    ))}
                  </ChipContainer>
                </InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.defaultContentCategory && (
              <InfoRow>
                <InfoIcon>
                  <Star sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Default Content Category</InfoLabel>
                <InfoValue>{mentorProfileData.defaultContentCategory}</InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.masteredMeditationIds?.length > 0 && (
              <InfoRow>
                <InfoIcon>
                  <Verified sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Mastered Meditations</InfoLabel>
                <InfoValue>
                  {mentorProfileData.masteredMeditationIds.length} meditation(s)
                </InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.meditationIds?.length > 0 && (
              <InfoRow>
                <InfoIcon>
                  <Book sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Meditation IDs</InfoLabel>
                <InfoValue>
                  {mentorProfileData.meditationIds.length} meditation(s)
                </InfoValue>
              </InfoRow>
            )}
          </SectionContent>
        </SectionCard>
      )}

      {/* Availability & Scheduling Section */}
      {(mentorProfileData?.schedules?.length > 0 || mentorProfileData?.eventTypes?.length > 0) && (
        <SectionCard>
          <SectionHeader>
            <Schedule sx={{ color: '#6366f1', fontSize: 24 }} />
            <SectionTitle>Availability & Scheduling</SectionTitle>
          </SectionHeader>
          <SectionContent>
            {mentorProfileData?.schedules?.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1.5, color: '#6b7280', fontSize: '14px' }}
                >
                  Schedules
                </Typography>
                {mentorProfileData.schedules.map((schedule, idx) => {
                  const scheduleData = formatScheduleDisplay(schedule)
                  const isManaged = !!schedule?.contextId
                  return (
                    <Box
                      key={idx}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: '8px',
                        bgcolor: isManaged ? 'rgba(245, 158, 11, 0.1)' : 'rgba(99, 102, 241, 0.05)',
                        border: `1px solid ${isManaged ? 'rgba(245, 158, 11, 0.2)' : 'rgba(99, 102, 241, 0.1)'}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 1.5,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            sx={{
                              fontFamily: "'Inter', sans-serif",
                              fontSize: '15px',
                              fontWeight: 600,
                            }}
                          >
                            {scheduleData.title}
                          </Typography>
                          {scheduleData.isDefault && (
                            <Chip
                              label="Default"
                              size="small"
                              sx={{
                                bgcolor: '#6366f1',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '10px',
                                height: '20px',
                              }}
                            />
                          )}
                          {scheduleData.timeZone && (
                            <Typography
                              variant="caption"
                              sx={{ color: '#6b7280', fontSize: '12px' }}
                            >
                              ({scheduleData.timeZone})
                            </Typography>
                          )}
                        </Box>
                        {isManaged && (
                          <Chip
                            label="Managed by Clinic Brand"
                            size="small"
                            sx={{
                              bgcolor: 'rgba(245, 158, 11, 0.2)',
                              color: '#f59e0b',
                              fontWeight: 600,
                              fontSize: '11px',
                            }}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {scheduleData.items.map((item, itemIdx) => (
                          <Box
                            key={itemIdx}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              py: 0.5,
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: "'Inter', sans-serif",
                                fontSize: '13px',
                                fontWeight: 500,
                                minWidth: '100px',
                              }}
                            >
                              {item.day}:
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "'Inter', sans-serif",
                                fontSize: '13px',
                                color: item.available ? '#1f2937' : '#9ca3af',
                                flex: 1,
                              }}
                            >
                              {item.slots}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            )}
            {mentorProfileData?.eventTypes?.length > 0 && (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, mb: 1.5, color: '#6b7280', fontSize: '14px' }}
                >
                  Bookable Services (Event Types)
                </Typography>
                {mentorProfileData.eventTypes.map((eventType, idx) => {
                  const eventData = formatEventType(eventType)
                  if (!eventData) return null
                  
                  const isManaged = !!eventData.contextId
                  return (
                    <Box
                      key={idx}
                      sx={{
                        p: 1.5,
                        mb: 1.5,
                        borderRadius: '8px',
                        bgcolor: isManaged ? 'rgba(245, 158, 11, 0.1)' : 'rgba(99, 102, 241, 0.05)',
                        border: `1px solid ${isManaged ? 'rgba(245, 158, 11, 0.2)' : 'rgba(99, 102, 241, 0.1)'}`,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          mb: 1,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                            <Typography
                              sx={{
                                fontFamily: "'Inter', sans-serif",
                                fontSize: '15px',
                                fontWeight: 600,
                              }}
                            >
                              {eventData.title}
                            </Typography>
                            <Chip
                              label={eventData.typeDisplayName}
                              size="small"
                              sx={{
                                bgcolor: '#6366f1',
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '11px',
                                height: '22px',
                              }}
                            />
                            {eventData.meetingType && (
                              <Chip
                                label={eventData.meetingType}
                                size="small"
                                icon={
                                  eventData.meetingSrc === 'aumhum' ? (
                                    <VideoLibrary sx={{ fontSize: 14 }} />
                                  ) : (
                                    <LocationOn sx={{ fontSize: 14 }} />
                                  )
                                }
                                sx={{
                                  bgcolor: eventData.meetingSrc === 'aumhum' ? '#10b981' : '#f59e0b',
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '11px',
                                  height: '22px',
                                }}
                              />
                            )}
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                              flexWrap: 'wrap',
                              mt: 0.5,
                            }}
                          >
                            {eventData.duration && (
                              <Typography
                                variant="caption"
                                sx={{ color: '#6b7280', fontSize: '12px', display: 'flex', alignItems: 'center', gap: 0.5 }}
                              >
                                <AccessTime sx={{ fontSize: 14 }} />
                                {eventData.duration}
                              </Typography>
                            )}
                            {eventData.price && (
                              <Typography
                                variant="caption"
                                sx={{ color: '#6b7280', fontSize: '12px', display: 'flex', alignItems: 'center', gap: 0.5 }}
                              >
                                <MonetizationOn sx={{ fontSize: 14 }} />
                                {eventData.price}
                              </Typography>
                            )}
                            {eventData.bookingWindow && (
                              <Typography
                                variant="caption"
                                sx={{ color: '#6b7280', fontSize: '12px' }}
                              >
                                Booking window: {eventData.bookingWindow}
                              </Typography>
                            )}
                          </Box>
                          {eventData.specialization && (
                            <Typography
                              variant="caption"
                              sx={{ color: '#6366f1', fontSize: '12px', display: 'block', mt: 0.5 }}
                            >
                              Specialization: {eventData.specialization}
                            </Typography>
                          )}
                        </Box>
                        {isManaged && (
                          <Chip
                            label="Managed by Clinic Brand"
                            size="small"
                            sx={{
                              bgcolor: 'rgba(245, 158, 11, 0.2)',
                              color: '#f59e0b',
                              fontWeight: 600,
                              fontSize: '11px',
                              ml: 1,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            )}
          </SectionContent>
        </SectionCard>
      )}

      {/* Listing & Channel Toggles Section */}
      {(mentorProfileData?.listableInApp !== undefined ||
        mentorProfileData?.listableInWeb !== undefined ||
        mentorProfileData?.exploreListable !== undefined ||
        mentorProfileData?.showMyProfileAs) && (
        <SectionCard>
          <SectionHeader>
            <Visibility sx={{ color: '#6366f1', fontSize: 24 }} />
            <SectionTitle>Listing & Channel Visibility</SectionTitle>
          </SectionHeader>
          <SectionContent>
            <InfoRow>
              <InfoIcon>
                <Public sx={{ fontSize: 20 }} />
              </InfoIcon>
              <InfoLabel>Show Profile As</InfoLabel>
              <InfoValue>
                {mentorProfileData?.showMyProfileAs || 'teacher'}
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoIcon>
                <Visibility sx={{ fontSize: 20 }} />
              </InfoIcon>
              <InfoLabel>Channel Visibility</InfoLabel>
              <InfoValue>
                <ChipContainer>
                  {mentorProfileData?.listableInApp && (
                    <StyledChip label="Listable in App" size="small" />
                  )}
                  {mentorProfileData?.listableInWeb && (
                    <StyledChip label="Listable in Web" size="small" />
                  )}
                  {mentorProfileData?.exploreListable && (
                    <StyledChip label="Explore Listable" size="small" />
                  )}
                  {mentorProfileData?.soundListable && (
                    <StyledChip label="Sound Listable" size="small" />
                  )}
                  {mentorProfileData?.storyListable && (
                    <StyledChip label="Story Listable" size="small" />
                  )}
                  {mentorProfileData?.sleepListable && (
                    <StyledChip label="Sleep Listable" size="small" />
                  )}
                  {mentorProfileData?.yogaListable && (
                    <StyledChip label="Yoga Listable" size="small" />
                  )}
                  {mentorProfileData?.journalListable && (
                    <StyledChip label="Journal Listable" size="small" />
                  )}
                  {mentorProfileData?.sdkListable && (
                    <StyledChip label="SDK Listable" size="small" />
                  )}
                </ChipContainer>
              </InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoIcon>
                <Settings sx={{ fontSize: 20 }} />
              </InfoIcon>
              <InfoLabel>Feature Toggles</InfoLabel>
              <InfoValue>
                <ChipContainer>
                  {mentorProfileData?.favorite && (
                    <StyledChip icon={<Favorite sx={{ fontSize: 16 }} />} label="Favorite" size="small" />
                  )}
                  {mentorProfileData?.donationEnabled && (
                    <StyledChip
                      icon={<MonetizationOn sx={{ fontSize: 16 }} />}
                      label="Donation Enabled"
                      size="small"
                    />
                  )}
                  {mentorProfileData?.chatEnabled && (
                    <StyledChip
                      icon={<Chat sx={{ fontSize: 16 }} />}
                      label="Chat Enabled"
                      size="small"
                    />
                  )}
                  {mentorProfileData?.planWelcome && (
                    <StyledChip label="Plan Welcome" size="small" />
                  )}
                  {mentorProfileData?.courseWelcome && (
                    <StyledChip label="Course Welcome" size="small" />
                  )}
                </ChipContainer>
              </InfoValue>
            </InfoRow>
          </SectionContent>
        </SectionCard>
      )}

      {/* Compliance & Approvals Section (Read-Only) */}
      {mentorProfileData?.teacherUnEditableFields && isTeacher && !isCreator && (
        <SectionCard>
          <SectionHeader>
            <Lock sx={{ color: '#6366f1', fontSize: 24 }} />
            <SectionTitle>Compliance & Approvals (Read-Only)</SectionTitle>
          </SectionHeader>
          <SectionContent>
            <Box sx={{ mb: 2, p: 2, bgcolor: 'rgba(99, 102, 241, 0.05)', borderRadius: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Info sx={{ fontSize: 16 }} />
                These fields are controlled by approval workflows and cannot be edited directly.
              </Typography>
            </Box>
            {mentorProfileData.teacherUnEditableFields?.showPayoutReports !== undefined && (
              <InfoRow>
                <InfoIcon>
                  <MonetizationOn sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Show Payout Reports</InfoLabel>
                <InfoValue>
                  {mentorProfileData.teacherUnEditableFields.showPayoutReports ? (
                    <Chip icon={<CheckCircle />} label="Enabled" size="small" color="success" />
                  ) : (
                    <Chip icon={<Cancel />} label="Disabled" size="small" color="default" />
                  )}
                </InfoValue>
              </InfoRow>
            )}
            {mentorProfileData.teacherUnEditableFields?.showFeeModels !== undefined && (
              <InfoRow>
                <InfoIcon>
                  <Settings sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Show Fee Models</InfoLabel>
                <InfoValue>
                  {mentorProfileData.teacherUnEditableFields.showFeeModels ? (
                    <Chip icon={<CheckCircle />} label="Enabled" size="small" color="success" />
                  ) : (
                    <Chip icon={<Cancel />} label="Disabled" size="small" color="default" />
                  )}
                </InfoValue>
              </InfoRow>
            )}
            {mentorProfileData.teacherUnEditableFields?.canOnlyViewTheirOwnPatients !== undefined && (
              <InfoRow>
                <InfoIcon>
                  <Visibility sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>View Own Patients Only</InfoLabel>
                <InfoValue>
                  {mentorProfileData.teacherUnEditableFields.canOnlyViewTheirOwnPatients ? (
                    <Chip icon={<CheckCircle />} label="Yes" size="small" color="warning" />
                  ) : (
                    <Chip icon={<Cancel />} label="No" size="small" color="default" />
                  )}
                </InfoValue>
              </InfoRow>
            )}
            {mentorProfileData.teacherUnEditableFields?.servicesApproved &&
              Object.keys(mentorProfileData.teacherUnEditableFields.servicesApproved).length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 600, mb: 1.5, color: '#6b7280', fontSize: '14px' }}
                  >
                    Approved Services
                  </Typography>
                  {/* Show roles first */}
                  {getRoleLabels().length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: '#6b7280', fontSize: '12px', mb: 0.5, display: 'block' }}
                      >
                        Roles:
                      </Typography>
                      <ChipContainer>
                        {getRoleLabels().map((role) => (
                          <StyledChip key={role} label={role} size="small" />
                        ))}
                      </ChipContainer>
                    </Box>
                  )}
                  {/* Show approved services grouped by role */}
                  {Object.entries(mentorProfileData.teacherUnEditableFields.servicesApproved).map(
                    ([key, service]) => {
                      if (!service?.enabled) return null
                      const serviceType = service.serviceType || 'N/A'
                      // Map serviceType to role labels
                      const roleLabel =
                        serviceType === 'doctorConsultation'
                          ? 'Doctor'
                          : serviceType === 'consultationTherapy'
                            ? 'Therapist'
                            : serviceType.includes('screening')
                              ? 'Screener'
                              : null

                      return (
                        <Box
                          key={key}
                          sx={{
                            p: 1.5,
                            mb: 1.5,
                            borderRadius: '8px',
                            bgcolor: 'rgba(99, 102, 241, 0.05)',
                            border: '1px solid rgba(99, 102, 241, 0.1)',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Typography
                              sx={{
                                fontFamily: "'Inter', sans-serif",
                                fontSize: '14px',
                                fontWeight: 600,
                              }}
                            >
                              {key}
                            </Typography>
                            {roleLabel && (
                              <Chip
                                label={roleLabel}
                                size="small"
                                sx={{
                                  bgcolor: '#6366f1',
                                  color: 'white',
                                  fontWeight: 600,
                                  fontSize: '11px',
                                }}
                              />
                            )}
                            <Chip
                              label={serviceType}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(99, 102, 241, 0.1)',
                                color: '#6366f1',
                                fontWeight: 500,
                                fontSize: '11px',
                              }}
                            />
                          </Box>
                          {service?.emrBasedReportTypes?.length > 0 && (
                            <Box sx={{ mb: 0.5 }}>
                              <Typography
                                variant="caption"
                                sx={{ color: '#6b7280', fontSize: '11px', display: 'block', mb: 0.5 }}
                              >
                                EMR Report Types:
                              </Typography>
                              <ChipContainer>
                                {service.emrBasedReportTypes.map((reportType) => (
                                  <StyledChip key={reportType} label={reportType} size="small" />
                                ))}
                              </ChipContainer>
                            </Box>
                          )}
                          {service?.specializations?.length > 0 && (
                            <Box sx={{ mb: 0.5 }}>
                              <Typography
                                variant="caption"
                                sx={{ color: '#6b7280', fontSize: '11px', display: 'block', mb: 0.5 }}
                              >
                                Specializations:
                              </Typography>
                              <ChipContainer>
                                {service.specializations.map((spec) => (
                                  <StyledChip key={spec} label={spec} size="small" />
                                ))}
                              </ChipContainer>
                            </Box>
                          )}
                          {service?.targetSpecialitySlugs?.length > 0 && (
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{ color: '#6b7280', fontSize: '11px', display: 'block', mb: 0.5 }}
                              >
                                Target Specialty Slugs:
                              </Typography>
                              <ChipContainer>
                                {service.targetSpecialitySlugs.map((slug) => (
                                  <StyledChip key={slug} label={slug} size="small" />
                                ))}
                              </ChipContainer>
                            </Box>
                          )}
                        </Box>
                      )
                    },
                  )}
                </Box>
              )}
            {mentorProfileData.teacherUnEditableFields?.approvedTherapyDisciplines?.length > 0 && (
              <InfoRow>
                <InfoIcon>
                  <Psychology sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Approved Therapy Disciplines</InfoLabel>
                <InfoValue>
                  <ChipContainer>
                    {mentorProfileData.teacherUnEditableFields.approvedTherapyDisciplines.map((d) => (
                      <StyledChip key={d} label={therapyDisciplineLabel(d)} size="small" />
                    ))}
                  </ChipContainer>
                </InfoValue>
              </InfoRow>
            )}
            {mentorProfileData.teacherUnEditableFields?.approvedSpecialitySlugs?.length > 0 && (
              <InfoRow>
                <InfoIcon>
                  <Verified sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Approved Specialty Slugs</InfoLabel>
                <InfoValue>
                  <ChipContainer>
                    {mentorProfileData.teacherUnEditableFields.approvedSpecialitySlugs.map((slug) => (
                      <StyledChip key={slug} label={slug} size="small" />
                    ))}
                  </ChipContainer>
                </InfoValue>
              </InfoRow>
            )}
          </SectionContent>
        </SectionCard>
      )}

      {/* Prescription & EMR Settings Section */}
      {isTeacher &&
        !isCreator &&
        (mentorProfileData?.allowedToPrescribe !== undefined ||
          mentorProfileData?.activePrescriptionTemplate ||
          mentorProfileData?.sendNotStartedBookingsDuringPrescriptionUpload !== undefined ||
          mentorProfileData?.showReportsRelatedDataInBookingCalendar !== undefined) && (
          <SectionCard>
          <SectionHeader>
            <LocalHospital sx={{ color: '#6366f1', fontSize: 24 }} />
            <SectionTitle>Prescription & EMR Settings</SectionTitle>
          </SectionHeader>
          <SectionContent>
            {mentorProfileData?.allowedToPrescribe !== undefined && (
              <InfoRow>
                <InfoIcon>
                  <Verified sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Allowed to Prescribe</InfoLabel>
                <InfoValue>
                  {mentorProfileData.allowedToPrescribe ? (
                    <Chip icon={<CheckCircle />} label="Yes" size="small" color="success" />
                  ) : (
                    <Chip icon={<Cancel />} label="No" size="small" color="default" />
                  )}
                </InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.activePrescriptionTemplate && (
              <InfoRow>
                <InfoIcon>
                  <Description sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Active Prescription Template</InfoLabel>
                <InfoValue>{mentorProfileData.activePrescriptionTemplate}</InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.sendNotStartedBookingsDuringPrescriptionUpload !== undefined && (
              <InfoRow>
                <InfoIcon>
                  <Settings sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Send Not Started Bookings</InfoLabel>
                <InfoValue>
                  {mentorProfileData.sendNotStartedBookingsDuringPrescriptionUpload ? (
                    <Chip label="Enabled" size="small" color="success" />
                  ) : (
                    <Chip label="Disabled" size="small" color="default" />
                  )}
                </InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.showReportsRelatedDataInBookingCalendar !== undefined && (
              <InfoRow>
                <InfoIcon>
                  <CalendarToday sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Show Reports in Calendar</InfoLabel>
                <InfoValue>
                  {mentorProfileData.showReportsRelatedDataInBookingCalendar ? (
                    <Chip label="Enabled" size="small" color="success" />
                  ) : (
                    <Chip label="Disabled" size="small" color="default" />
                  )}
                </InfoValue>
              </InfoRow>
            )}
          </SectionContent>
        </SectionCard>
      )}

      {/* Associations Section */}
      {(mentorProfileData?.afflliationIds?.length > 0 ||
        mentorProfileData?.instituteIds?.length > 0 ||
        mentorProfileData?.affiliations?.length > 0 ||
        mentorProfileData?.instituteName) && (
        <SectionCard>
          <SectionHeader>
            <Business sx={{ color: '#6366f1', fontSize: 24 }} />
            <SectionTitle>Associations</SectionTitle>
          </SectionHeader>
          <SectionContent>
            {mentorProfileData?.instituteName && (
              <InfoRow>
                <InfoIcon>
                  <School sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Institute Name</InfoLabel>
                <InfoValue>{mentorProfileData.instituteName}</InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.schoolId && (
              <InfoRow>
                <InfoIcon>
                  <School sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>School ID</InfoLabel>
                <InfoValue>{mentorProfileData.schoolId}</InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.afflliationIds?.length > 0 && (
              <InfoRow>
                <InfoIcon>
                  <Business sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Affiliation IDs</InfoLabel>
                <InfoValue>
                  {mentorProfileData.afflliationIds.length} affiliation(s)
                </InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.instituteIds?.length > 0 && (
              <InfoRow>
                <InfoIcon>
                  <School sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Institute IDs</InfoLabel>
                <InfoValue>
                  {mentorProfileData.instituteIds.length} institute(s)
                </InfoValue>
              </InfoRow>
            )}
            {mentorProfileData?.ancilliaryRoles?.length > 0 && (
              <InfoRow>
                <InfoIcon>
                  <Work sx={{ fontSize: 20 }} />
                </InfoIcon>
                <InfoLabel>Ancillary Roles</InfoLabel>
                <InfoValue>
                  <ChipContainer>
                    {mentorProfileData.ancilliaryRoles.map((role) => (
                      <StyledChip key={role} label={role} size="small" />
                    ))}
                  </ChipContainer>
                </InfoValue>
              </InfoRow>
            )}
          </SectionContent>
        </SectionCard>
      )}

      {/* Translations Section */}
      {mentorProfileData?.translations &&
        Object.keys(mentorProfileData.translations).length > 0 && (
          <SectionCard>
            <SectionHeader>
              <Translate sx={{ color: '#6366f1', fontSize: 24 }} />
              <SectionTitle>Translations</SectionTitle>
            </SectionHeader>
            <SectionContent>
              {Object.entries(mentorProfileData.translations)
                .filter(([lang, data]) => data?.firstName) // Only show languages with firstName
                .map(([lang, data]) => (
                  <Accordion key={lang} sx={{ mb: 1, boxShadow: 'none', border: '1px solid rgba(0,0,0,0.1)' }}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Language sx={{ fontSize: 18, color: '#6366f1' }} />
                        <Typography fontWeight={600}>
                          {lang.toUpperCase()} - {data?.firstName} {data?.lastName}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {data?.shortDesc && (
                          <InfoRow>
                            <InfoLabel>Short Description</InfoLabel>
                            <InfoValue>{data.shortDesc}</InfoValue>
                          </InfoRow>
                        )}
                        {data?.longDesc && (
                          <InfoRow>
                            <InfoLabel>Long Description</InfoLabel>
                            <InfoValue sx={{ whiteSpace: 'pre-wrap' }}>{data.longDesc}</InfoValue>
                          </InfoRow>
                        )}
                        {data?.welcomeMsg && (
                          <InfoRow>
                            <InfoLabel>Welcome Message</InfoLabel>
                            <InfoValue>{data.welcomeMsg}</InfoValue>
                          </InfoRow>
                        )}
                        {data?.location && (
                          <InfoRow>
                            <InfoLabel>Location</InfoLabel>
                            <InfoValue>
                              {[data.location.city, data.location.state, data.location.country]
                                .filter(Boolean)
                                .join(', ')}
                            </InfoValue>
                          </InfoRow>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
            </SectionContent>
          </SectionCard>
        )}

      {/* Profile URL Section */}
      {mentorProfileData?.slug && (
        <SectionCard>
          <SectionHeader>
            <Public sx={{ color: '#6366f1', fontSize: 24 }} />
            <SectionTitle>Public Profile</SectionTitle>
          </SectionHeader>
          <SectionContent>
            <InfoRow>
              <InfoIcon>
                <Public sx={{ fontSize: 20 }} />
              </InfoIcon>
              <InfoLabel>Profile URL</InfoLabel>
              <InfoValue>
                <CertificateLink
                  href={`https://aumhum.com/mentor/${mentorProfileData.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  aumhum.com/mentor/{mentorProfileData.slug}
                </CertificateLink>
              </InfoValue>
            </InfoRow>
          </SectionContent>
        </SectionCard>
      )}
    </ProfileViewerContainer>
  )
}

// Memoize to prevent re-renders when props haven't changed
export default memo(ProfileViewer, (prevProps, nextProps) => {
  // Only re-render if profileData or allTags actually changed
  const profileDataChanged = prevProps.profileData !== nextProps.profileData
  const allTagsChanged = prevProps.allTags !== nextProps.allTags
  const onEditChanged = prevProps.onEdit !== nextProps.onEdit
  
  // Return true if props are equal (don't re-render), false if different (re-render)
  return !(profileDataChanged || allTagsChanged || onEditChanged)
})
